import React, { useState, useEffect, useMemo } from 'react';
import {
  FaCalendarAlt, FaTimes, FaMoneyBillWave, FaWhatsapp, FaSearch, FaFileExport,
  FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Helmet } from "react-helmet-async";
import axiosInstance from '../../api/axiosInstance';
import Pagination from '../Pagination/Pagination';
import './ReminderEncouragementFund.css';

const EF_TYPE = 'Encouragement Fund (प्रोत्साहन निधि)';
const MODES = ['Cash', 'Cheque', 'UPI', 'Bank Transfer', 'NEFT', 'RTGS',];
const PAGE_OPTIONS = [10, 20, 30, 40, 50, 100];

const ReminderEncouragementFund = () => {
  const [donors, setDonors] = useState([]);
  const [renewModal, setRenewModal] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All'); // New Year Filter State

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Added payment date and receiptNo to default state
  const [paymentForm, setPaymentForm] = useState({ 
    mode: 'Cash', 
    refNo: '', 
    date: new Date().toISOString().split('T')[0], 
    receiptNo: '' 
  });
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => { fetchDonors(); }, []);

  const fetchDonors = async () => {
    try {
      const res = await axiosInstance.get('/api/donors');
      setDonors(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (err) {
      console.error("Failed to fetch donors for reminder", err);
    }
  };

  // Step 1: Base Processing
  const baseProcessedDonors = useMemo(() => {
    const list = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    donors.forEach(donor => {
      if (donor.isDeleted) return;

      const efDonations = (donor.donations || []).filter(
        d => d.donationType === EF_TYPE && (d.txnStatus === 'Success' || !d.txnStatus)
      );
      if (efDonations.length === 0) return;

      // Anchor = first payment's exact day+month
      efDonations.sort((a, b) => new Date(a.date) - new Date(b.date));
      const anchor = new Date(efDonations[0].date);
      const totalPayments = efDonations.length;

      // Expiry = anchor day+month + totalPayments years
      // e.g. first pay 1 Mar 2024, 3 payments => expiry = 1 Mar 2027
      const expiryDate = new Date(anchor);
      expiryDate.setFullYear(anchor.getFullYear() + totalPayments);
      expiryDate.setHours(0, 0, 0, 0);

      const daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24));

      // Show only when expiry is within 60 days or already expired
      if (daysLeft <= 60) {
        list.push({
          ...donor,
          expiryDate,
          expiryYear: expiryDate.getFullYear().toString(),
          daysLeft,
          nextCoverYear: expiryDate.getFullYear()
        });
      }
    });

    return list;
  }, [donors]);

  // Step 2: Extract Unique Expiry Years dynamically
  const availableYears = useMemo(() => {
    const years = [...new Set(baseProcessedDonors.map(d => d.expiryYear))];
    return years.sort((a, b) => b - a); // Sort descending
  }, [baseProcessedDonors]);

  // Step 3: Apply All Filters (Search, Status, Year)
  const expiringDonors = useMemo(() => {
    let list = [...baseProcessedDonors];

    // Filter by Expiry Year
    if (yearFilter !== 'All') {
      list = list.filter(d => d.expiryYear === yearFilter);
    }

    // Filter by Search Query
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(d =>
        (d.name && d.name.toLowerCase().includes(q)) ||
        (d.mobileNo && d.mobileNo.includes(q)) ||
        (d.memberId && d.memberId.toLowerCase().includes(q))
      );
    }

    // Filter by Status
    if (statusFilter === 'Expired') list = list.filter(d => d.daysLeft < 0);
    else if (statusFilter === 'Due') list = list.filter(d => d.daysLeft >= 0);

    return list.sort((a, b) => a.daysLeft - b.daysLeft);
  }, [baseProcessedDonors, searchQuery, statusFilter, yearFilter]);

  // Reset pagination on filter change
  useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilter, yearFilter]);

  const paginatedDonors = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return expiringDonors.slice(startIndex, startIndex + itemsPerPage);
  }, [expiringDonors, currentPage, itemsPerPage]);

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const handleOpenRenew = (donor) => {
    setRenewModal(donor);
    setPaymentForm({ 
      mode: 'Cash', 
      refNo: '', 
      date: new Date().toISOString().split('T')[0],  // today = actual payment date
      receiptNo: '' 
    });
  };

  const handleProcessRenew = async () => {
    if (!renewModal) return;
    
    // VALIDATE THE NEW CUSTOM FIELDS
    if (!paymentForm.date) {
      return Swal.fire('Warning', 'Payment Date is required.', 'warning');
    }
    if (!paymentForm.receiptNo || paymentForm.receiptNo.trim() === '') {
      return Swal.fire('Warning', 'Receipt No (रसीद नं.) is required.', 'warning');
    }

    // REQUIRE REF NO FOR NON-CASH TRANSACTIONS
    if (paymentForm.mode !== 'Cash' && (!paymentForm.refNo || paymentForm.refNo.trim() === '')) {
       return Swal.fire('Warning', 'Ref / Cheque No is required for this payment mode.', 'warning');
    }

    try {
      const payload = {
        donationType: EF_TYPE, 
        amount: 5000, 
        date: paymentForm.date, 
        mode: paymentForm.mode, 
        refNo: paymentForm.refNo,
        receiptNo: paymentForm.receiptNo
      };

      await axiosInstance.post(`/api/donors/${renewModal._id}/donate`, payload);

      Swal.fire('Success', 'Encouragement Fund Renewed. The expiry has been extended by 1 year.', 'success');
      setRenewModal(null);
      fetchDonors();
    } catch (err) {
      Swal.fire('Error', 'Failed to process renewal.', 'error');
    }
  };

  const handleWhatsApp = (donor) => {
    let phone = donor.mobileNo.replace(/\D/g, '');
    if (phone.length === 10) phone = '91' + phone;

    const message = `जय माता जी ,श्रीमान ${donor.name},\n\nराजपूत शिक्षा प्रोत्साहन निधि का वार्षिक सदस्यता शुल्क बकाया है। कृपया भेजने की कृपा करावें।\n\nऑनलाइन भुगतान के लिए यहाँ क्लिक करें:\nhttps://rajputshikshakosh.com/donate-protsahan-nidhi\n\nधन्यवाद!`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
  };

  const handleExportExcel = async () => {
    if (expiringDonors.length === 0) return Swal.fire('Notice', 'No data to export.', 'info');

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('EF Reminders');

    worksheet.mergeCells('A1:G1');
    worksheet.getCell('A1').value = 'RAJPUT SHIKSHA KOSH';
    worksheet.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: 'FFB91C1C' } };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A2:G2');
    worksheet.getCell('A2').value = 'प्रोत्साहन निधि Reminder List';
    worksheet.getCell('A2').font = { name: 'Arial', size: 12, bold: true, color: { argb: 'FF2C3E50' } };
    worksheet.getCell('A2').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.mergeCells('A3:G3');
    worksheet.getCell('A3').value = `Search: ${searchQuery || 'None'} | Status: ${statusFilter} | Expiry Year: ${yearFilter}`;
    worksheet.getCell('A3').font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF64748B' } };
    worksheet.getCell('A3').alignment = { vertical: 'middle', horizontal: 'center' };

    worksheet.addRow([]);

    const headerRow = worksheet.addRow(['S.No', 'Member ID', 'Name', 'Contact Number', 'Expiry Date', 'Next Cover Year', 'Status']);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF34495E' } };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    expiringDonors.forEach((donor, i) => {
      const statusText = donor.daysLeft < 0 ? `Expired (${Math.abs(donor.daysLeft)} days ago)` : `Due in ${donor.daysLeft} days`;
      const row = worksheet.addRow([
        i + 1, donor.memberId || 'N/A', donor.name, donor.mobileNo,
        donor.expiryDate.toLocaleDateString('en-IN'),
        donor.nextCoverYear,
        statusText
      ]);

      row.eachCell((cell) => {
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });
      row.getCell(3).alignment = { horizontal: 'left' };

      const statusCell = row.getCell(7);
      statusCell.font = { bold: true, color: { argb: donor.daysLeft < 0 ? 'FFDC2626' : 'FFD97706' } };
    });

    worksheet.columns = [{ width: 8 }, { width: 15 }, { width: 25 }, { width: 15 }, { width: 15 }, { width: 16 }, { width: 22 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'Encouragement_Fund_Reminders.xlsx');
  };

  return (
    <>
    <Helmet>
                <title>Reminder Encouragement Fund | Rajput Shiksha Kosh</title>
        
                <meta
                  name="description"
                  content="View and manage encouragement fund reminders for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="admin-reminder-ef-container">
      <h2 className="admin-reminder-ef-title">
        <FaCalendarAlt style={{ marginRight: '10px' }} /> प्रोत्साहन निधि Reminders
      </h2>

      <div className="admin-reminder-ef-controls">
        <div className="admin-reminder-ef-search-wrapper">
          <FaSearch className="admin-reminder-ef-search-icon" />
          <input
            type="text"
            className="admin-reminder-ef-search-input"
            placeholder="Search by Name or Contact..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="admin-reminder-ef-action-group">
          {/* NEW YEAR FILTER */}
          <select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} className="admin-reminder-ef-limit-select">
            <option value="All">Filter by Expiry Year (All)</option>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="admin-reminder-ef-limit-select">
            <option value="All">All Reminders</option>
            <option value="Expired">Expired Only</option>
            <option value="Due">Due in Next 60 Days</option>
          </select>
          
          <select className="admin-reminder-ef-limit-select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
            {PAGE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
          </select>
          
          <button className="admin-reminder-ef-btn-export" onClick={handleExportExcel} title="Export Styled Excel">
            <FaFileExport /> Export Excel
          </button>
        </div>
      </div>

      <div className="admin-reminder-ef-table-wrapper">
        <table className="admin-reminder-ef-tbl">
          <thead>
            <tr>
              <th className="col-sno">S.No</th>
              <th className="col-memberid">Member ID</th>
              <th className="col-name admin-reminder-ef-align-left">Name</th>
              <th className="col-contact">Contact No</th>
              <th className="col-expiry">Expiry Date</th>
              <th className="col-status">Status</th>
              <th className="col-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {expiringDonors.length === 0 ? (
              <tr><td colSpan="7" className="admin-reminder-ef-empty">No reminders found at this time.</td></tr>
            ) : paginatedDonors.map((donor, i) => {
              const globalIndex = (currentPage - 1) * itemsPerPage + i + 1;
              const isExpanded = expandedRow === donor._id;

              return (
                <React.Fragment key={donor._id}>
                  <tr className="admin-reminder-ef-main-row">
                    <td className="col-sno fw-bold">{globalIndex}</td>

                    <td className="col-memberid fw-bold" style={{ color: donor.memberId ? "#334155" : "#e67e22" }}>
                      {donor.memberId || 'N/A'}
                    </td>

                    <td className="col-name admin-reminder-ef-align-left">
                      <div className="admin-reminder-ef-name-wrapper" onClick={() => toggleRow(donor._id)}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{donor.name}</span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }} className="mobile-contact-span">{donor.mobileNo}</span>
                        </div>
                        <button className="admin-reminder-ef-expand-btn">
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>

                    <td className="col-contact">{donor.mobileNo}</td>

                    <td className="col-expiry fw-bold text-primary">
                      {donor.expiryDate.toLocaleDateString('en-IN')}
                    </td>

                    <td className="col-status">
                      {donor.daysLeft < 0 ? (
                        <span className="admin-reminder-ef-badge expired">Expired {-donor.daysLeft} d ago</span>
                      ) : (
                        <span className="admin-reminder-ef-badge warning">{donor.daysLeft} Days Left</span>
                      )}
                    </td>

                    <td className="col-action">
                      <div className="admin-reminder-ef-action-btns">
                        <button className="admin-reminder-ef-btn-renew" onClick={() => handleOpenRenew(donor)} title="Renew Fund">
                          <FaMoneyBillWave /> Renew
                        </button>
                        <button className="admin-reminder-ef-btn-whatsapp" onClick={() => handleWhatsApp(donor)} title="Send WhatsApp">
                          <FaWhatsapp /> Send Msg
                        </button>
                      </div>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr className="admin-reminder-ef-expanded-row">
                      <td colSpan="7">
                        <div className="admin-reminder-ef-expanded-content">
                          <div className="admin-reminder-ef-exp-item exp-memberid">
                            <span className="admin-reminder-ef-exp-label">Member ID</span>
                            <span className="admin-reminder-ef-exp-value" style={{ color: donor.memberId ? '#334155' : '#e67e22' }}>{donor.memberId || 'N/A'}</span>
                          </div>
                          
                          <div className="admin-reminder-ef-exp-item exp-contact">
                            <span className="admin-reminder-ef-exp-label">Contact No</span>
                            <span className="admin-reminder-ef-exp-value">{donor.mobileNo}</span>
                          </div>
                          
                          <div className="admin-reminder-ef-exp-item exp-expiry">
                            <span className="admin-reminder-ef-exp-label">Expiry Date</span>
                            <span className="admin-reminder-ef-exp-value fw-bold text-primary">{donor.expiryDate.toLocaleDateString('en-IN')}</span>
                          </div>
                          
                          <div className="admin-reminder-ef-exp-item exp-status">
                            <span className="admin-reminder-ef-exp-label">Status</span>
                            <span className="admin-reminder-ef-exp-value">
                              {donor.daysLeft < 0 ? (
                                <span className="admin-reminder-ef-badge expired">Expired {-donor.daysLeft} d ago</span>
                              ) : (
                                <span className="admin-reminder-ef-badge warning">{donor.daysLeft} Days Left</span>
                              )}
                            </span>
                          </div>
                          
                          <div className="admin-reminder-ef-exp-item exp-action">
                            <span className="admin-reminder-ef-exp-label">Action</span>
                            <div className="admin-reminder-ef-action-btns" style={{ justifyContent: 'flex-start' }}>
                              <button className="admin-reminder-ef-btn-renew" onClick={() => handleOpenRenew(donor)}>
                                <FaMoneyBillWave /> Renew
                              </button>
                              <button className="admin-reminder-ef-btn-whatsapp" onClick={() => handleWhatsApp(donor)}>
                                <FaWhatsapp /> Send Msg
                              </button>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {expiringDonors.length > 0 && (
        <Pagination
          totalItems={expiringDonors.length}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}

      {renewModal && (
        <div className="admin-reminder-ef-modal-overlay">
          <div className="admin-reminder-ef-modal">
            <div className="admin-reminder-ef-modal-header">
              <h3 className="admin-reminder-ef-modal-title">Renew Encouragement Fund</h3>
              <FaTimes className="admin-reminder-ef-close-icon" onClick={() => setRenewModal(null)} />
            </div>

            <div className="admin-reminder-ef-modal-body">
              <div className="admin-reminder-ef-info-card">
                <p><strong>Member:</strong> <span className="text-dark">{renewModal.name}</span> ({renewModal.memberId || 'N/A'})</p>
                <p><strong>Current Expiry:</strong> <span className="text-primary">{renewModal.expiryDate.toLocaleDateString('en-IN')}</span></p>
                <p><strong>This Payment Covers Year:</strong> <span className="text-success">{renewModal.nextCoverYear}</span></p>
              </div>

              <div className="admin-reminder-ef-form-group mt-3">
                <label>Amount (₹)</label>
                <input type="number" className="admin-reminder-ef-input readonly" value="5000" readOnly />
                <small className="admin-reminder-ef-hint">Fixed amount for 1 continuous year extension.</small>
              </div>

              {/* Added Payment Date input option */}
              <div className="admin-reminder-ef-form-group mt-3">
                <label>Payment Date <span className="admin-reminder-ef-req">*</span></label>
                <input 
                  type="date" 
                  className="admin-reminder-ef-input" 
                  value={paymentForm.date} 
                  onChange={e => setPaymentForm({ ...paymentForm, date: e.target.value })} 
                  required 
                />
              </div>

              {/* Added Receipt No (रसीद नं.) input option */}
              <div className="admin-reminder-ef-form-group mt-3">
                <label>Receipt No (रसीद नं.) <span className="admin-reminder-ef-req">*</span></label>
                <input 
                  type="text" 
                  className="admin-reminder-ef-input" 
                  value={paymentForm.receiptNo} 
                  onChange={e => setPaymentForm({ ...paymentForm, receiptNo: e.target.value })} 
                  placeholder="e.g. RSK-ADM-002" 
                  required 
                />
              </div>

              <div className="admin-reminder-ef-form-group mt-3">
                <label>Payment Mode <span className="admin-reminder-ef-req">*</span></label>
                <select className="admin-reminder-ef-input" value={paymentForm.mode} onChange={e => setPaymentForm({ ...paymentForm, mode: e.target.value })}>
                  {MODES.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              {paymentForm.mode !== 'Cash' && (
                <div className="admin-reminder-ef-form-group mt-3">
                  <label>Ref / Cheque No <span className="admin-reminder-ef-req">*</span></label>
                  <input type="text" className="admin-reminder-ef-input" value={paymentForm.refNo} onChange={e => setPaymentForm({ ...paymentForm, refNo: e.target.value })} placeholder="Reference Details" required />
                </div>
              )}
            </div>

            <div className="admin-reminder-ef-modal-footer">
              <button className="admin-reminder-ef-btn-cancel" onClick={() => setRenewModal(null)}>Cancel</button>
              <button className="admin-reminder-ef-btn-confirm" onClick={handleProcessRenew}>Confirm Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default ReminderEncouragementFund;